<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

// Cho anh ve xong thi bong bong da co san dung khoang cua no, khong bi bung ra
// mot cai lam danh sach tin nhay. Khong biet ti le that thi lay 4:3.
const aspectRatio = computed(() => {
  const { width, height } = attachment.value ?? {};
  return width && height ? `${width} / ${height}` : '4 / 3';
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <!-- BonBoZ: anh di mot minh.
       Vien mau tu 12px xuong 4px — 12px bien moi tam anh thanh mot cai khung
       tranh day cop, ma bong bong anh thi khong co chu de ma dem. 4px vua du de
       con nhan ra mau bong bong (xanh la = trang gui, trang = khach gui).
       Anh bi chan chieu cao o 22rem: anh doc dien thoai khach chup cao 4000px
       thi khong con chiem tron man hinh, phai cuon ba vong moi qua duoc mot tin.
       Cho tai thi giu san cho theo ti le that thay vi khong ve gi — truoc day
       bong bong bung ra khi anh ve xong, day tut ca danh sach tin. -->
  <BaseBubble
    class="overflow-hidden p-1"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div
      v-if="hasError"
      class="flex gap-1 items-center px-2 py-1 text-center rounded-lg"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else-if="!isLoaded"
      class="w-64 max-w-full rounded-xl animate-pulse bg-n-alpha-2"
      :style="{ aspectRatio }"
    />
    <div v-else class="overflow-hidden relative group rounded-xl">
      <img
        class="block object-contain w-auto max-w-full h-auto max-h-[22rem] skip-context-menu"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
        decoding="async"
      />
      <div
        class="hidden absolute inset-0 p-2 bg-gradient-to-tl pointer-events-none from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent group-hover:flex"
      />
      <div class="hidden absolute right-2 bottom-2 gap-2 group-hover:flex">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
